/**
 * Subs & Vendors API — address book + trust score list/detail.
 */

import { apiFetch } from './client'
import type { ContractorVendor, ProjectVendorRow } from './types'
import { fetchContractorVendors, fetchProjects } from './projects'
import type { TrustScoreData, TrustTierName } from '../components/trust/types'
import { MAX_TRUST_SCORE } from '../components/trust/types'

export interface ContractorTrustScoreListRow {
  id:                    number
  contractor_user_id:    number
  vendor_user_id:        number
  computed_score:        number
  override_score:        number | null
  is_visible_to_vendor:  boolean
  last_event_at:         string | null
  vendor_name:           string | null
  vendor_email:          string | null
}

export interface TrustScoreEvent {
  id:                   number
  trust_score_id:       number
  event_type:           string
  delta:                number
  context:              Record<string, unknown> | null
  held_harmless:        boolean
  held_harmless_reason: string | null
  created_at:           string
}

export interface TrustCoachingMessage {
  headline:  string
  detail:    string
  sentiment: 'positive' | 'neutral' | 'negative'
}

export interface TrustCoachingResponse {
  tier:       TrustTierName
  tierLabel:  string
  coaching:   TrustCoachingMessage
}

export interface SubVendorListItem {
  vendor:         ContractorVendor
  vendorUserId:   number | null
  trustScore:     TrustScoreData | null
  status:         'active' | 'invited' | 'book'
  projectCount:   number
}

const TIER_LABELS: Record<TrustTierName, string> = {
  platinum: 'Platinum',
  gold:     'Gold',
  silver:   'Silver',
  bronze:   'Bronze',
  review:   'Review',
}

function tierFromScore(score: number): TrustTierName {
  const s = Math.max(0, Math.min(MAX_TRUST_SCORE, score))
  if (s >= 687) return 'platinum'
  if (s >= 534) return 'gold'
  if (s >= 381) return 'silver'
  if (s >= 229) return 'bronze'
  return 'review'
}

export function mapTrustListRow(row: ContractorTrustScoreListRow): TrustScoreData {
  const effectiveScore = row.override_score ?? row.computed_score
  const tier = tierFromScore(effectiveScore)
  return {
    id:                row.id,
    computedScore:     row.computed_score,
    effectiveScore,
    overrideScore:     row.override_score,
    isOverridden:      row.override_score != null,
    tier,
    tierLabel:         TIER_LABELS[tier],
    isVisibleToVendor: row.is_visible_to_vendor,
    lastEventAt:       row.last_event_at,
  }
}

export async function fetchContractorTrustScores(): Promise<ContractorTrustScoreListRow[]> {
  const res = await apiFetch<{ data: ContractorTrustScoreListRow[] }>(
    'GET',
    '/api/trust-score/',
  )
  return res.data ?? []
}

async function fetchProjectVendorRows(projectId: number): Promise<ProjectVendorRow[]> {
  const res = await apiFetch<{ data: ProjectVendorRow[] }>(
    'GET',
    `/api/contractor/projects/${projectId}/vendors`,
  )
  return res.data ?? []
}

/** Aggregate project membership + status per address-book vendor id. */
export async function fetchVendorProjectStats(): Promise<
  Map<number, { projectCount: number; status: 'active' | 'invited' }>
> {
  const projects = await fetchProjects()
  const map = new Map<number, { projectCount: number; hasActive: boolean }>()

  await Promise.all(projects.map(async (p) => {
    try {
      const rows = await fetchProjectVendorRows(p.id)
      for (const row of rows) {
        const prev = map.get(row.vendor_id) ?? { projectCount: 0, hasActive: false }
        map.set(row.vendor_id, {
          projectCount: prev.projectCount + 1,
          hasActive:    prev.hasActive || row.status === 'active',
        })
      }
    } catch {
      /* skip projects we cannot read */
    }
  }))

  const out = new Map<number, { projectCount: number; status: 'active' | 'invited' }>()
  for (const [vendorId, v] of map) {
    out.set(vendorId, {
      projectCount: v.projectCount,
      status:       v.hasActive ? 'active' : 'invited',
    })
  }
  return out
}

export async function buildSubVendorList(): Promise<SubVendorListItem[]> {
  const [vendors, trustRows, projectStats] = await Promise.all([
    fetchContractorVendors(),
    fetchContractorTrustScores(),
    fetchVendorProjectStats(),
  ])

  const trustByEmail = new Map<string, ContractorTrustScoreListRow>()
  for (const row of trustRows) {
    if (row.vendor_email) {
      trustByEmail.set(row.vendor_email.toLowerCase(), row)
    }
  }

  return vendors.map((vendor) => {
    const emailKey = vendor.email.toLowerCase()
    const trustRow = trustByEmail.get(emailKey) ?? null

    const stats = projectStats.get(vendor.id)
    const vendorUserId = trustRow?.vendor_user_id ?? null

    let status: SubVendorListItem['status'] = 'book'
    if (stats) {
      status = stats.status
    } else if (trustRow) {
      status = 'active'
    }

    return {
      vendor,
      vendorUserId,
      trustScore: trustRow ? mapTrustListRow(trustRow) : null,
      status,
      projectCount: stats?.projectCount ?? 0,
    }
  })
}

export async function fetchTrustScoreEvents(vendorUserId: number): Promise<TrustScoreEvent[]> {
  const res = await apiFetch<{ data: TrustScoreEvent[] }>(
    'GET',
    `/api/trust-score/${vendorUserId}/events`,
  )
  return res.data ?? []
}

export async function fetchTrustCoaching(vendorUserId: number): Promise<TrustCoachingResponse | null> {
  try {
    const res = await apiFetch<{ data: TrustCoachingResponse }>(
      'GET',
      `/api/trust-score/${vendorUserId}/coaching`,
    )
    return res.data ?? null
  } catch {
    return null
  }
}

export interface ResendPortalLinkResult {
  invite_url: string
  invite_code: string
  email: string
  project_id: number
}

export async function resendPortalLink(vendorId: number, projectId?: number): Promise<ResendPortalLinkResult> {
  const res = await apiFetch<{ data: ResendPortalLinkResult }>(
    'POST',
    `/api/contractor/vendors/${vendorId}/resend-portal-link`,
    projectId ? { project_id: projectId } : {},
  )
  return res.data
}

export { fetchContractorVendors }
